import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { QueryConfig } from "@/lib/react-query";
import { ScenarioBuilderClient } from "@/pages/scenario-builder/api/client/scenarioClient";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";
import {
  ActionPlanDetail,
  ActionPlanLifecycleStatus,
  ActionPlanStatus,
  ActionPlanSummary,
  ScenarioBase,
  ScenarioSummary,
} from "@mutiny-group/sbo-openapi-client";
import { useQuery } from "@tanstack/react-query";
import { compact, isEmpty, last } from "lodash";
import { safeCalculatePercentage } from "@/shared/utils/calculatePercentage";
import { calculateDurationProgress } from "@/pages/scenario-builder/v3/pages/action-plans/helpers/calculateDurationProgress";
import dayjs from "dayjs";

type GetActionPlanOptions = {
  scenarioBuilderClient: ScenarioBuilderClient;
  actionPlanId: string;
};

// Use the detailed action plan endpoint instead of the summary endpoint
const getActionPlan = ({ scenarioBuilderClient, actionPlanId }: GetActionPlanOptions) => {
  return scenarioBuilderClient.actionPlans.getActionPlanDetails({
    actionPlanUuid: actionPlanId,
    cumulative: true,
    groupBy: ["period_start"],
  });
};

type UseGetActionPlanOptions = {
  actionPlanId: string;
  config?: QueryConfig<typeof getActionPlan>;
};

export type ExtendedActionPlanDetail = ActionPlanDetail & {
  scopeBrandForDisplay: string;
  liveScenario: ScenarioBase | null;
  refreshDates: Date[] | null;
  durationPercentage: number;
  spendPercentage: number;
  revenuePercentage: number;
  currentRevenueDifference: number;
  isActionPlanRefreshed: boolean;
  isActionPlanRefreshing: boolean;
  isActionPlanComplete: boolean;
};

/**
 * Transform an ActionPlanDetail into an ExtendedActionPlanDetail with all computed properties.
 * This function is used both in the query hook and in test fixtures to ensure consistency.
 */
export const transformActionPlanToExtended = (
  actionPlan: ActionPlanDetail,
  mapDefaultNodeName: (name: string) => string
): ExtendedActionPlanDetail => {
  const {
    startDate,
    endDate,
    status,
    approvedScenarios,
    historicalSpendRevenueAttribution,
    futureSpendRevenueAttribution,
    liveRevenueForecast,
    revenueTarget,
    recommendations,
    lifecycleStatus,
  } = actionPlan;

  const isActionPlanComplete = status === ActionPlanStatus.Complete;

  // If the action plan is complete, the updated at is the end date. Otherwise, it is the last historical spend revenue attribution period start date, or the start date if there are no entries.
  const getActualUpdatedAt = () => {
    if (isActionPlanComplete) return endDate;
    if (!historicalSpendRevenueAttribution || isEmpty(historicalSpendRevenueAttribution)) {
      return startDate;
    }
    return last(historicalSpendRevenueAttribution)?.periodStart ?? startDate;
  };

  const updatedAt = getActualUpdatedAt();

  // Live scenario is the last entry in the approved scenarios list
  const liveScenario =
    approvedScenarios?.length > 0 ? (last(approvedScenarios)?.scenario ?? null) : null;

  // Refresh dates are the start dates of the approved scenarios
  const refreshDates =
    approvedScenarios?.length > 0
      ? compact(actionPlan.approvedScenarios.map((scenario) => scenario.scenario?.startDate))
      : null;

  // Only include future spend revenue attribution entries that are after the last historical entry
  const getFilteredSpendRevenueAttribution = () => {
    const lastHistoricalEntry = last(actionPlan.historicalSpendRevenueAttribution);
    if (!lastHistoricalEntry || !lastHistoricalEntry?.periodStart) {
      return futureSpendRevenueAttribution;
    }
    return futureSpendRevenueAttribution.filter((entry) =>
      dayjs(entry.periodStart).isAfter(dayjs(lastHistoricalEntry.periodStart))
    );
  };

  const { spendPercentage, revenuePercentage } = getExtendedActionPlanDetails(actionPlan);

  return {
    ...actionPlan,
    updatedAt,
    futureSpendRevenueAttribution: getFilteredSpendRevenueAttribution(),
    durationPercentage: calculateDurationProgress(startDate, endDate, updatedAt),
    scopeBrandForDisplay: mapDefaultNodeName(actionPlan.scopeBrand ?? "default"),
    spendPercentage,
    revenuePercentage,
    currentRevenueDifference: liveRevenueForecast - revenueTarget,
    liveScenario,
    refreshDates,
    isActionPlanRefreshed: actionPlan.historicalSpendRevenueAttribution.length > 0,
    isActionPlanRefreshing: lifecycleStatus === ActionPlanLifecycleStatus.UpdateInProgress,
    isActionPlanComplete,
    recommendations: getRecommendations(recommendations),
  };
};

export const useActionPlan = ({ actionPlanId, config }: UseGetActionPlanOptions) => {
  const scenarioBuilderClient = useScenarioBuilderClient();
  const { mapDefaultNodeName } = useDefaultNodeNameInfo();

  return useQuery({
    ...config,
    queryKey: ["action-plan", actionPlanId, scenarioBuilderClient],
    queryFn: () => getActionPlan({ scenarioBuilderClient, actionPlanId }),
    enabled: !!actionPlanId,
    select: (data): ExtendedActionPlanDetail =>
      transformActionPlanToExtended(data, mapDefaultNodeName),
  });
};

export const getExtendedActionPlanDetails = (actionPlan: ActionPlanDetail | ActionPlanSummary) => {
  const { spendActualToDate, spendPlannedTotal, revenueActualToDate, revenueTarget } = actionPlan;
  return {
    spendPercentage: safeCalculatePercentage(spendActualToDate, spendPlannedTotal),
    revenuePercentage: safeCalculatePercentage(revenueActualToDate, revenueTarget),
  };
};

// Filters out invalid recommendations
const getRecommendations = (recommendations: ScenarioSummary[]) =>
  recommendations.filter(
    (recommendation) =>
      recommendation?.scenario &&
      recommendation?.scenario?.uuid &&
      recommendation?.scenario?.risk &&
      recommendation?.scenario?.risk?.strategyProfile
  );
